import { ColDef, ColumnState, GridOptions, _ } from 'ag-grid-community';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { IDeferred } from 'angular';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerExportFormat } from 'src/shared/models/module-manager/module-manager-options.model';
import { ModuleManagerModuleSettings } from 'src/shared/models/module-manager/module-manager-settings.models';
import { WebhelpETSData } from './webhelp-ets.model';
import { WebhelpETSService } from './webhelp-ets.service';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
    selector: 'cv-webhelp-custom',
    templateUrl: './webhelp-ets.component.html',
    styleUrls: ['./webhelp-ets.component.scss']
})
export class WebhelpETSComponent implements OnInit {
    @Input() moduleKey: string;
    @Input() moduleManager: ModuleManager;
    @ViewChild('webhelpEtsGrid') agGrid: AgGridAngular;

    public gridOptions: GridOptions;
    private columnDefs: ColDef[];
    private _loadAborter: IDeferred<any>;
    private _stateSaveTimer: any;
    private _settingsSaveTimer: any;
    private data: WebhelpETSData[];
    private prevData: WebhelpETSData[];
    private isFirstLoad = true;
    private loadedColumnState: ColumnState[];
    private loadedFilterModel: any;
    private columnDisplayOrder: string[] = [];
    private excludedColumns: string[] = [];
    private distinctColumns: string[];
    private gridState: any = null;

    constructor(
		private agGridHelperService: AgGridHelperService,
		private service: WebhelpETSService
    ) {
        this.moduleManager?.setMaxDateRange(32); // TODO: I can probably have this use the DB
    }

    ngOnInit(): void {
        this.initializeGrid();

        this.moduleManager.setOptions({
            hasRefresh: true,
            onSettingsApplied: this.onSettingsApplied.bind(this),
            onRefresh: this.onRefresh.bind(this),
            onExport: this.onExport.bind(this),
        });

        /** Ag Grid column state */
        this.moduleManager.addState({
            name: 'columnState',
            autoLoad: true,
            getStateData: () => {
                if (!this.agGrid?.api)
                    // eslint-disable-next-line no-throw-literal
                    throw false; // throw because we are not ready to save state

                return this.agGrid?.api.getColumnState();
            },
            onStateLoaded: (stateData) => {
                if (stateData == null)
                    return;

                this.loadedColumnState = stateData;

                if (this.agGrid?.api) {
                    this.agGrid?.api.applyColumnState({ state: this.loadedColumnState });
                }
            }
        });

        this.moduleManager.addState({
            name: "gridState",
            getStateData: () => {
                return this.gridState;
            },
            onStateLoaded: stateData => {
                if (!stateData) { // No table state loaded
                    return;
                }
                this.gridState = stateData;
                if (this.agGrid?.api && this.gridState)
                    this.agGridHelperService.loadGridState(this.agGrid.api, stateData);
            },
            autoLoad: true
        });

        /** Ag Grid filters */
        this.moduleManager.addState({
            name: 'filterModel',
            autoLoad: true,
            getStateData: () => {
                if (!this.agGrid?.api)
                    // eslint-disable-next-line no-throw-literal
                    throw false; // throw because we are not ready to save state

                return this.agGrid?.api.getFilterModel();
            },
            onStateLoaded: (stateData) => {
                if (stateData == null)
                    return;

                this.loadedFilterModel = stateData;
            }
        });

        /** Display order for the columns -- different from column def order */
        this.moduleManager.addState({
            name: 'columnOrder',
            autoLoad: true,
            getStateData: () => {
                if (!this.agGrid?.api)
                    // eslint-disable-next-line no-throw-literal
                    throw false; // throw because we are not ready to save state

                const columns: string[] = [];
                this.agGrid?.api.getAllDisplayedColumns().forEach(col => {
                    columns.push(col.getColId());
                });
                return columns;
            },
            onStateLoaded: (stateData) => {
                if (stateData == null) {
                    return;
                }

                this.columnDisplayOrder = stateData;
            }
        });

        /** ETS_KEY values to filter out. Kept separate to be accurate to the Columns custom setting. */
        this.moduleManager.addState({
            name: 'excludedColumns',
            autoLoad: true,
            getStateData: () => {
                if (!this.agGrid?.api)
                    // eslint-disable-next-line no-throw-literal
                    throw false; // throw because we are not ready to save state

                const filter = this.agGrid?.api.getFilterInstance("ETS_KEY");
                const valueModel = filter["valueModel"];
                if (valueModel?.allValues) {
                    const notSelected: string[] = valueModel.allValues.filter(v => !valueModel.selectedValues.has(v));
                    const valuesToAdd: string[] = notSelected.filter(v => !this.excludedColumns.includes(v));
                    this.excludedColumns = this.excludedColumns.filter(v => !valueModel.selectedValues.has(v));
                    this.excludedColumns = this.excludedColumns.concat(valuesToAdd);
                }

                return this.excludedColumns;
            },
            onStateLoaded: (stateData) => {
                if (stateData == null) {
                    return;
                }

                this.excludedColumns = stateData;
            }
        });

        /** Distinct ETS_KEY values from the loaded data, for use with the Columns setting */
        this.moduleManager.addState({
            name: 'distinctColumnsFromData',
            autoLoad: true,
            getStateData: () => {
                if (!this.agGrid?.api)
                    // eslint-disable-next-line no-throw-literal
                    throw false; // throw because we are not ready to save state

                return this.data.map(row => row.ETS_KEY).filter((value, index, self) => self.indexOf(value) === index);
            },
            onStateLoaded: (stateData) => {
                if (stateData == null) {
                    return;
                }

                this.distinctColumns = stateData;
            }
        });

        this.moduleManager.customSettingsAdded = true;
    }

    async initializeGrid() {
        const queueStateSave = () => this.queueStateSave();
        this.columnDefs = this.loadColumnsFromSettings();
        this.loadedFilterModel = await this.loadFilterFromSettings();

        this.gridOptions = this.agGridHelperService.createGridOptions({
            columnDefs: this.columnDefs,
            context: {
                excludedColumns: this.excludedColumns
            },
            defaultColDef: {
                resizable: true,
                suppressAutoSize: false,
                suppressSizeToFit: false,
                flex: 1,
            },
            autoGroupColumnDef: {
                flex: 2
            },
            columnTypes: {
                aggColumn: {
                    aggFunc: 'avg',
                    enableRowGroup: false,
                    enableValue: true,
                    valueFormatter: params => {
                        if (params.value?.toNumber) {
                            const formattedValue = params.value.toNumber()?.toFixed(2);
                            return formattedValue ? formattedValue : 0;
                        }
                    },
                },
                groupColumn: {
                    rowGroup: true,
                    enableRowGroup: true,
                    enableValue: false,
                    filter: true
                }
            },
            sideBar: {
                toolPanels: [
                    {
                        id: 'columns',
                        labelDefault: 'Columns',
                        labelKey: 'columns',
                        iconKey: 'columns',
                        toolPanel: 'agColumnsToolPanel',
                        toolPanelParams: {
                            suppressPivotMode: true,
                            suppressPivots: true,
                        }
                    },
                    {
                        id: 'filters',
                        labelDefault: 'Filters',
                        labelKey: 'filters',
                        iconKey: 'filters',
                        toolPanel: 'agFiltersToolPanel',
                    }
                ]
            },

            onGridReady: params => {
                if (this.data) {
                    this.agGrid?.api.setGridOption('rowData', this.data);
                }
                this.restoreColumnState();
                if (!this.gridState) {
                    this.agGridHelperService.sizeColumnsSmart(params);
                    this.tableStateChanged();
                }
            },

            pivotMode: true,
            functionsReadOnly: false,

            onSortChanged: event => {
                this.queueStateSave();
                if (event.source === "uiColumnSorted")
                    this.moduleManager.requestPublishNeeded();
            },
            onColumnPivotChanged: queueStateSave,
            onColumnPivotModeChanged: queueStateSave,
            onFilterChanged: queueStateSave,
            onColumnResized: queueStateSave,
            onColumnMoved: event => {
                this.queueStateSave();
                if (event.source === "uiColumnMoved")
                    this.moduleManager.requestPublishNeeded();
            },
            onColumnPinned: event => {
                this.queueStateSave();
                if (event.source === "contextMenu")
                    this.moduleManager.requestPublishNeeded();
            },
            onColumnVisible: event => {
                this.queueStateSave();
                if (event.source === "uiColumnMoved")
                    this.moduleManager.requestPublishNeeded();
            },
            onColumnRowGroupChanged: queueStateSave,
            onColumnValueChanged: queueStateSave,
        }, {
            enableCopy: true
        });
    }

    loadColumnsFromSettings(): ColDef[] {
        const settings = this.moduleManager?.getModuleSettings();
        if (settings?.groupBy == null || settings?.dataColumns == null)
            return [];

        const selectedColumnDefs: string[] = settings.dataColumns.concat(settings.groupBy);

        const availableColumnDefs: ColDef[] = [
            {
                field: "AGENTSECONDS",
                type: 'aggColumn'
            },
            {
                field: "ACWSECONDS",
                type: 'aggColumn'
            },
            {
                field: "AHTSECONDS",
                type: 'aggColumn'
            },
            {
                field: "ETS_WRAPUPCATEGORY",
                type: 'groupColumn'
            },
            {
                field: "ETS_WRAPUPCODE",
                type: 'groupColumn'
            },
            {
                field: "ETS_KEY",
                type: 'groupColumn'
            },
            {
                field: "ETS_VALUE",
                type: 'groupColumn'
            }
        ];

        const colDefs: ColDef[] = [];
        availableColumnDefs.forEach(element => {
            // eslint-disable-next-line eqeqeq
            if (selectedColumnDefs?.find((s) => element.field == s))
                colDefs.push(element);
        });

        return colDefs;
    }

    /** Make sure the filter from state matches with the Columns setting */
    async loadFilterFromSettings() {
        const settings = this.moduleManager?.getModuleSettings();
        if (settings?.columnsSelected == null)
            return;

        if (this.agGrid?.api) {
            // ETS_KEY filters can be set through the module settings, but the other filters are just through the grid interface
            let loadedFilterModel = await this.moduleManager.previousStateValues['filterModel'];
            if (loadedFilterModel) {
                if (typeof (loadedFilterModel["ETS_KEY"]) === "undefined")
                    loadedFilterModel["ETS_KEY"] = { values: settings.columnsSelected };
                else
                    loadedFilterModel["ETS_KEY"]["values"] = settings.columnsSelected;
            }
            else
                loadedFilterModel = { ETS_KEY: { values: settings.columnsSelected } };

            return loadedFilterModel;
        }
    }

    async onSettingsApplied(settings: ModuleManagerModuleSettings) {
        this.columnDefs = this.loadColumnsFromSettings();
        this.loadedFilterModel = await this.loadFilterFromSettings();
        if (this.agGrid?.api) {
            this.agGrid?.api.setGridOption('columnDefs', this.columnDefs);
            if (this.loadedFilterModel) { // null means no filter
                this.agGrid?.api.setFilterModel(this.loadedFilterModel);
                this.agGrid?.api.onFilterChanged();
            }
        }
        this.loadData(false);
    }

    onRefresh(isAutoRefresh: boolean) {
        this.loadData(isAutoRefresh);
    }

    async loadData(isAutoRefresh: boolean) {
        if (this._loadAborter != null)
            this._loadAborter.resolve();

        try {
            this.moduleManager.showLoader = !isAutoRefresh;
            const response = await this.service.getWebhelpETSData(this.moduleKey, isAutoRefresh, this.isFirstLoad);
            this.prevData = this.data;
            this.data = response;
        } catch (e) {
            this.moduleManager.handleLoadError(e, isAutoRefresh);
            return;
        } finally {
            this._loadAborter = null;
        }

        this.moduleManager.showLoader = false;
        this.updateGrid();
        this.isFirstLoad = false;
    }

    updateGrid() {
        if (!this.data)
            return;

        if (this.agGrid?.api) {
            let state: ColumnState[];
            let filterModel: any;

            if (!this.isFirstLoad) { // capture state before resetting columns
                state = this.agGrid?.api.getColumnState();
                filterModel = this.agGrid?.api.getFilterModel();
            }

            if (this.data.length > 20000) // don't do a transaction if there are a lot of rows
                this.agGrid?.api.setGridOption('rowData', this.data);
            else
                this.agGrid?.api.applyTransaction(this.agGridHelperService.createTransaction(this.prevData, this.data, this.gridOptions));

            this.prevData = null;

            if (this.isFirstLoad) {
                if (typeof (this.loadedColumnState) !== "undefined") {
                    this.agGrid?.api.applyColumnState({ state: this.loadedColumnState, applyOrder: true });
                    // order row groups
                    this.loadedColumnState.forEach(col => {
                        if (col.rowGroup === true) {
                            this.agGrid?.api.moveRowGroupColumn(col.rowGroupIndex, col.rowGroupIndex); // apply state didn't really order these
                        }
                    });

                    // display order not saved by column state
                    this.agGrid?.api.moveColumns(this.columnDisplayOrder, 0);
                }

                if (typeof (this.loadedFilterModel) !== "undefined") {
                    this.agGrid?.api.setFilterModel(this.loadedFilterModel);
                    this.agGrid?.api.onFilterChanged();
                }
            }
        } else {
            this.gridOptions.rowData = this.data;
            this.gridOptions.columnDefs = this.columnDefs;
        }
    }

    /**
     * Get formated data ready to export
     * @param format
     */
    public async onExport(format: ModuleManagerExportFormat) {
        const sort = {
            order: []
        };
        // Ag Grid update V30.0.0 removed getSortModel
        // const agState = this.agGrid?.api.getSortModel();
        const agState = this.agGrid?.api.getColumnState();
        for (const state of agState) {
            sort.order[state.sortIndex] = [Number(state.colId), state.sort];
        }
        const response = await this.service.getDataForExport(this.moduleKey, format, JSON.stringify(sort));
        return response;
    }

    async queueStateSave() {
        if (this._stateSaveTimer != null)
            clearTimeout(this._stateSaveTimer);

        this._stateSaveTimer = setTimeout(() => {
            this.moduleManager.saveState(
                'columnState', 'sortModel', 'filterModel', 'columnOrder', 'excludedColumns', 'distinctColumnsFromData');
            this._stateSaveTimer = null;
        }, 500, false);
    }

    restoreColumnState() {
        if (this.agGrid.api && this.gridState)
            this.agGridHelperService.loadGridState(this.agGrid.api, this.gridState);
    };

    tableStateChanged() {
        this.gridState = this.agGridHelperService.getGridState(this.agGrid.api);
        this.queueStateSave();
    }
}
